import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Tag } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const releases = [
  {
    version: 'v8.60.7',
    date: '2026-09-19',
    headline: 'Local AI sorts finish instead of stalling, and updating from inside the app works again on Mac',
    items: [
      'Sorting with your own local model (Ollama or LM Studio) no longer stalls near the end, showing the same file over and over. Some local models keep writing after the plan is finished, listing files again or starting the list over. Sortio now stops reading at that point and shows the plan for review.',
      'Stop ends a local sort right away, including in the middle of the model\'s reply. Before, it only took effect between batches, so on a small folder it did nothing.',
      'If a local model leaves files out of its plan, Sortio asks it once more for just those files instead of dropping them.',
      'Updating from inside the app works again on macOS. Updates published since 8.58.4 failed to install with an "unpack" error, and downloading from the website was the only way through. Sortio 8.60.6 was republished with the same fix, so this update installs normally.',
      'The Feedback button works on the screen the app opens on. Before, sending feedback there failed with a page reference error until you visited another page first.',
    ],
  },
  {
    version: 'v8.60.6',
    date: '2026-09-19',
    headline: 'Upgrading mid-read picks up where it stopped',
    items: [
      'Upgrading after the free trial\'s file reading runs out now picks the reading up where it stopped. Before, the app did no more AI work until it was restarted, so "Analyze contents" did nothing right after paying.',
      'The upgrade prompt says what actually ran out. When only the part of the trial set aside for reading files is used, it says so, and that the rest is kept for sorting. It closes itself once the upgrade goes through, and it appears once instead of once per window.',
      'Leaving the Sort page while a sort is reading files, designing folders, planning or moving files now asks first. "Keep sorting" stays; "Stop and leave" stops the sort, and nothing it moved stays moved.',
      'A sort that ends up moving nothing no longer leaves a "0 files" entry in History, and History no longer shows an Undo button on an entry with nothing to undo. The first-run Apply button can no longer be pressed twice.',
      'The in-app assistant now explains that sorted files are really moved on disk, into subfolders you can see in Finder or File Explorer, and that a Space runs its automations rather than a sort prompt.',
      'When your own AI engine (Ollama or LM Studio) is not running, a sort now shows how to fix it or switch back to Sortio Cloud, instead of a raw network error.',
      'Large counts in "Put everything back" are grouped with thousands separators.',
      'First-time setup no longer switches on automatic filing of new files into Documents, Images and Archives folders. The folder you pick is still added as a Space for search, and automations are something you add yourself from the Automations page. Anyone who already has those starter automations keeps them.',
    ],
  },
  {
    version: 'v8.60.5',
    date: '2026-09-17',
    headline: 'Put everything back in one click',
    items: [
      'New: "Put everything back" in History reverses every sort Sortio has made, newest first, so a file two sorts moved comes back one hop at a time. It states what it will do before anything moves, can be limited to one folder, can be stopped at any point, and leaves a single History row. Nothing is deleted; files are only moved back.',
      'An Undo row no longer offers its own Undo button. The History list never returned the session type, so clicking Undo on an undo re-applied the sort it had just reversed. Undo now refuses to run on an undo.',
      'A sort whose undo was itself undone can be undone again, rather than showing a greyed-out button while the files sit scattered.',
      'Every History row now says what its last Undo did: "all restored", "366 of 368 restored", or "nothing to restore". Rows that had already been worked used to read exactly like untouched ones.',
      'Undo searches for files that have moved since in the right order: the folder the sort touched first and your home folder last, so a large library no longer fills the search index before the search reaches it.',
      'Sortio warns before a sort when the prompt asks for copies. Sortio moves files and has no copy action, and a prompt asking for organized copies with the originals untouched used to pass validation and run.',
      'When an AI allowance runs out the app says so and offers the upgrade, instead of retrying the denied request every couple of seconds in the background.',
    ],
  },
  {
    version: 'v8.60.1',
    date: '2026-09-17',
    headline: 'Automations no longer run on their own work',
    items: [
      'An automation set to run when files are added no longer acts on files Sortio itself just moved, renamed or restored. A busy folder, a large run, or an Undo could previously leave it re-filing its own output, which filled History with hundreds of one-file entries and left Undo unable to find the files.',
      'If something outside Sortio keeps putting a file back, automations now stop moving that file after a few tries and tell you, instead of trading moves with it forever.',
      'An automation that renames a file using its own name, or moves it relative to its own folder, no longer applies itself again to a file it already handled, so names stop growing and folders stop nesting deeper. Undo resets this, so running it again after an undo still works.',
      'Undoing a run inside a watched folder now sticks: the folder watcher no longer re-files what Undo just put back.',
      'Automation backups are kept apart from sort backups, so a busy automation can no longer push your sort backups out, and Restore from backup now works for an automation run.',
      'The in-app assistant now describes correctly what Sortio reads: OCR needs Analyze file contents turned on, that setting is off by default, and a File content condition reads the text already in a file.',
    ],
  },
  {
    version: 'v8.60.0',
    date: '2026-09-17',
    headline: 'Scanned PDFs and images are read more reliably',
    items: [
      'A content sort with content analysis turned off no longer sends your PDFs and scans to "_Needs Review" as unreadable when nothing ever tried to read them. They are placed by filename with a "name only" label, and if most of a plan was placed that way, Apply asks first and points you to Analyze contents.',
      'A file that fails to read is no longer retried on every sort with "Sort again to keep reading". After two failed attempts it is shown as could not be read, and it is tried again after an update or when the file changes.',
      'Sorting a folder no longer makes Sortio read again, and spend AI allowance on, files it had already read. What it learned about a file now moves with the file.',
      'On Mac, a damaged PDF, a file that is not really a PDF, or a password-protected PDF is recognized and set aside instead of being retried every time.',
      'PDF text reading was upgraded: more PDFs are read directly without falling back to OCR, and a PDF that makes the reader crash twice is skipped instead of crashing Sortio again.',
      'On Windows, iPhone HEIC photos are no longer sent to a text reader that cannot open them, unsupported or oversized images are skipped quickly, and a slow or locked PDF is no longer permanently marked as a blank scan.',
      '.tif and .jfif scans are now read, and so are .htm and .conf files.',
      'One failed AI request no longer stops Sortio from reading the rest of a folder before a sort.',
      'A rule\'s sync failure badge now clears once a later sync succeeds.',
      'Diagnostic reports about reading failures no longer include file names.',
    ],
  },
  {
    version: 'v8.59.3',
    date: '2026-09-16',
    headline: 'Buy more AI usage when you need it',
    items: [
      'Pro and Team subscribers who reach the monthly AI allowance can now add a one-time top-up, from the allowance notice or from Settings, Account. Two sizes: half a month\'s allowance or a full month\'s. A top-up is valid for 30 days, does not renew, and does not change your plan. It is applied within a minute of paying, and background indexing picks up where it paused.',
      'Undo now finds a file by its content when the trail of moves is broken. When a file is not where a sort left it and no later record explains where it went, undo checks your home folder, every Space and the sorted folder, matching by name, then size, then a fingerprint of the file. The preflight says how many files were found this way.',
      'History has a Newest first / Oldest first selector, so your early sorts are reachable without paging past hundreds of one-file automation runs.',
      'Three places that could destroy a file no longer can. Restore from backup never overwrites a file that differs from the backup (an edit made since the sort): the backup copy is placed next to it as \"(from backup)\". Choosing Overwrite in an undo sets the existing file aside and removes it only after the restored file is in place, so a failed move cannot lose it. And a move across drives, in a sort, an automation or an undo, copies to a temporary name and renames into place, so an interrupted copy leaves no half-written file and the original stays put.',
      'Sort backups and automation backups are now kept in separate windows, so a busy automation can no longer push every sort backup out of the ten most recent.',
    ],
  },
  {
    version: 'v8.58.6',
    date: '2026-09-15',
    headline: 'Undo follows a file through every sort on Windows',
    items: [
      'On Windows, each sort recorded where a file came from in a different spelling than where the previous sort had put it (a system prefix on one side and not the other). Undo matches those exactly, so it could not follow a file that a later sort had moved again and reported it as "could not be found". Sorts now record the plain spelling, existing history is repaired the first time you open this update, and undoing an older sort brings back files that later sorts had moved.',
    ],
  },
  {
    version: 'v8.58.4',
    date: '2026-09-14',
    headline: 'Sorting a library you already organized keeps it organized',
    items: [
      'Re-sorting a folder you had already filed by hand used to plan a move for every file, either up into a parent folder or into a brand new tree, because the sort could only see each file\'s parent folder name. It now knows where every file already sits, leaves the ones that are in the right place alone (they show as "stays put" in the preview, under the folder they are in), and moves only the files that need a home.',
      'The folder planner sized every folder like a document archive: for an 8,000-photo library it asked for 80 to 120 folders and would have rejected the eight categories its owner had named. Photos and videos are now sized by events and occasions, and the planner keeps every folder you already have.',
      'Photos were set aside in "_Needs Review" whenever a content sort found no text in them, which is every photo. A camera photo now stays in the sort and is placed by its date taken, camera and folder; scans and screenshots with no readable text are still set aside. Sorts read a photo\'s date taken and camera again, which they had quietly stopped doing.',
      'On Windows, reading scanned documents failed on any file whose path contained a character outside the English alphabet. Fixed.',
      'The tip after a sort, the prompt check and the in-app assistant no longer suggest that Sortio can see what is in a photo, and no longer hand you a prompt with a placeholder like [YOUR FOLDER NAME] in it.',
    ],
  },
  {
    version: 'v8.58.3',
    date: '2026-09-14',
    headline: 'Undo can follow an automation again on Windows',
    items: [
      'On Windows, files moved by an automation were recorded with a mix of forward and backward slashes, while the folder watcher reported the same files back with backslashes. Undo compares those paths exactly, so it could not follow a file from one automation run to the next and reported it as "could not be found". Paths are now stored consistently, existing history is repaired the first time you open this update, and undoing an automation run brings back files that later runs had moved again.',
      'The Undo dialog names the folder it is undoing. On Windows it showed the whole path, and for a folder nested many levels deep the title alone filled the dialog and pushed the Cancel and Confirm Undo buttons off the screen. The title now stays on one line, with the full path on hover.',
    ],
  },
  {
    version: 'v8.58.1',
    date: '2026-09-13',
    headline: 'Sorting a synced folder no longer downloads it first',
    items: [
      'When you sorted a folder that lives in iCloud, Dropbox or OneDrive, the step that designs folder structures opened every file it had never read to peek at its contents. In a synced folder most files are placeholders that are not on the disk, and opening one makes the sync client fetch it, so a big folder could sit on "Designing a few ways to organize this" for many minutes while files downloaded one by one. It now only reads files that are already on your machine and plans the rest from their names, and it tells you how many it left in the cloud.',
      'That planning card now shows what it is checking, with a count and a real progress bar, and offers "Skip suggestions and sort now" if it runs long.',
      'A file that was merely downloaded, restored from a backup or re-tagged, without being edited, no longer gets re-indexed by a Space.',
    ],
  },
  {
    version: 'v8.58.0',
    date: '2026-09-13',
    headline: 'Re-index finishes the whole folder, and automations stop guessing',
    items: [
      'Re-index in a Space used to stop after the first 500 files, and files it could not read (scans the text recognition got nothing from) were quietly picked again on every press, so a large folder never got past them. Re-index now keeps going until the whole folder is done, shows the real total, and remembers which files it already tried. A new "Re-read files with no details" item in the Space menu gives files that were indexed with nothing found another pass.',
      'When a scanned page is not legible, the place or name in the file name is often the only readable thing. Sortio was throwing that away, so a rename built on {entity.location} came back empty. It now keeps it. Template fallbacks work as written too: {file.exif.datetime.year|file.created.year} falls back to the creation year when there is no EXIF date instead of writing the words literally.',
      'Automatic rules now fire when a file is moved into the folder or renamed, wait for a file that is still being copied to finish, and never re-file a file an AI sort or an Undo just placed. A Space set to Manual runs nothing on its own.',
      'Run results tell the truth: a run that matched nothing says so, files already where they belong count as "already in place", failures are counted per file with the reason shown, and Undo appears only when something actually moved. The editor has a NOT toggle on every condition, refuses a regular expression that does not compile before saving, and shows imported conditions and action types it cannot run instead of silently swapping them.',
      'Discover: a pack you already imported says Imported, and one with a newer version offers Update, which asks first because it replaces your edits to those automations.',
      'Sorting a folder of more than about 3,000 files used to fall back silently to a coarse layout. Large folders now get a proper structure, and reading files before a sort no longer stops every 500 files to ask permission. You can also build your own folder structure and describe it. Legacy .doc files are now readable.',
      'Bring your own key: model IDs now come from one catalog, so a retired model no longer lands on the wrong slot or on your bill.',
      'A Feedback button in the sidebar lets you point at anything in the app and tell us about it.',
    ],
  },
  {
    version: 'v8.57.0',
    date: '2026-09-11',
    headline: 'Deleting an automation now actually deletes it',
    items: [
      'Deleting an automation used to remove it only from the machine you were sitting at. Your account still had it, so the next time the Automations page loaded it came straight back, switched on. A delete now removes it from your account, and it stays gone on every machine you use.',
      'Turning an automation off was saved only on that machine too, which is the other half of why a deleted one came back enabled. The switch now saves to your account.',
      'One thing worth knowing if you deleted automations before this update: those deletes left no record anywhere, so we have nothing to undo. You will still see those automations listed. Delete them once more and this time it sticks.',
    ],
  },
  {
    version: 'v8.56.0',
    date: '2026-09-10',
    headline: 'Automations ask before they reorganize a folder',
    items: [
      'An automation that files documents into a "Documents" folder now makes one folder in the folder you pointed it at, instead of one inside every subfolder it walked. If you meant a folder beside each file, write ./Documents, which still does exactly that.',
      'A run that would move more than a handful of files now stops first and shows you what it is about to do: how many files, which folders, which of those folders it would have to create. Nothing moves until you say so. Smaller runs are unchanged and still just run.',
      'Running the same automations twice no longer claims it is about to do work on files that are already where they belong.',
      'The "merge similar folders" suggestion now only offers to merge folders that sit side by side. It used to gather same-named folders from anywhere in the tree, which could pour one project\'s files into another\'s.',
    ],
  },
  {
    version: 'v8.51.3',
    date: '2026-08-23',
    headline: 'The free trial now finishes your first sort',
    items: [
      'On the free trial, pointing Sortio at a very large folder used to spend the entire trial allowance planning and deliver nothing. Trial sorts now handle up to 400 files per run: pick "Sort the 400 most recent" and the sort completes, with the rest of the folder left untouched. Paid plans keep the 8,000-file ceiling, and local AI / bring-your-own-key sorting is unaffected.',
      'The "that\'s a lot of files" screen now says what the trial limit is and what upgrading raises it to, instead of leaving the ceiling unexplained.',
    ],
  },
  {
    version: 'v8.51.2',
    date: '2026-08-23',
    headline: 'Rules Sortio writes now always run',
    items: [
      'Sortio generates automation rules in three places (the AI rule builder, Discover packs, and older saved rules), and each used slightly different spellings for fields, comparators and template variables. A mismatch failed silently: "0 matched", or every file failing at run time, with the rule looking fine in the editor. Every spelling Sortio has ever produced now resolves to one canonical form at run time, so rules already saved on your machine start working without a re-save.',
      'Rename patterns that name a folder (like {entity.org}/{file.stem}) now move and rename instead of being rejected, and sorting by extension makes a visible "pdf" folder rather than a hidden ".pdf" one.',
    ],
  },
  {
    version: 'v8.51.1',
    date: '2026-08-22',
    headline: 'Automations that explain themselves, and Clio matching rebuilt',
    items: [
      'When an automation matches nothing, the dry run now says why: each condition shows how many files it matched and what the field actually held on the ones it rejected. "File type" is now labelled "Media type (MIME)", which is the field most people were reaching for when they meant the extension.',
      'Several automation conditions quietly never matched and now do: "is one of" typed into the builder, "older than" and "newer than" (which take 30, 2w, 6m or 1y), and file sizes with units like 5MB. Comparators now fit the field, so dates offer before/after and extensions offer wildcards such as *.docx.',
      'A rename pattern that left out the file name used to drop the file\'s extension. The original extension is kept automatically, {file.stem} gives the name without it, and characters Windows refuses (such as : and ?) are removed instead of failing the file.',
      'The Enabled switch on the Automations page now saves, a turned-off automation says it is turned off instead of "nothing matched", and saving checks for a Move with no destination or a condition with no value before anything runs.',
      'Imported community automations: "file_type: docx" now means the extension, and negated conditions are honored.',
      'Matching files to Clio matters works properly now. Since the integration launched, the step that finds candidate matters demanded that a single matter contain every word of a filename, so it found nothing for almost every file and the rest of the matcher guessed from an unrelated list. On a test firm the right matter now reaches the shortlist 99.7% of the time; before, it was 0%.',
      'Folder names count as evidence. A scan called Scan_0042.pdf filed under Clients/Smith v Jones/Discovery matches on the folder chain, and folders up to eight levels deep are included (deeper levels used to be dropped without a word).',
      'With content analysis on, the document\'s own subject line is used, which is what tells two matters for the same client apart. Files with nothing to go on now show "pick a matter" instead of a low-confidence guess.',
      'Document categories now actually reach Clio. They were being sent under the wrong field name and Clio had been quietly ignoring them.',
      'Re-running a folder no longer reports files that are already in Clio as failures; they are left out of the preview and counted as already uploaded.',
      'If the Clio sign-in finishes in the browser but this Mac never gets the token, the card says so and offers to finish connecting instead of showing Connected over an empty matter list. The region picker now offers only the United States, which is where the integration is available.',
      'Bring-your-own-key users on gpt-5: sorts no longer fail with a temperature error.',
    ],
  },
  {
    version: 'v8.50.0',
    date: '2026-08-22',
    headline: 'Your plan is kept when the allowance runs out',
    items: [
      'If the monthly AI allowance runs out partway through a sort, Sortio now shows the plan it had already built instead of discarding it, so you can still review and apply what was finished.',
      'Onboarding completion was being counted as closing the app in our own metrics. No change for you; it fixes a number we watch.',
    ],
  },
  {
    version: 'v8.49.1',
    date: '2026-08-21',
    headline: 'Sortio now installs on older Macs',
    items: [
      'The macOS requirement has dropped from Ventura (13) down to Big Sur (11). If your Mac refused to install Sortio because it asked for macOS 13 or later, download it again and it will open.',
      'The requirement listed on our site had been wrong for some time, which is how people ended up downloading a build their Mac would not run. The download page now states the real requirement.',
    ],
  },
  {
    version: 'v8.49.0',
    date: '2026-08-20',
    headline: 'Quieter automations, no more stalled sorts',
    items: [
      'A watched folder that files a steady trickle of documents now shows one notification with a running total and a single Undo, instead of one notification per batch stacking up in the corner. Notifications also clear themselves, and a "Dismiss all" appears when several are open at once.',
      'A sort no longer freezes if the machine sleeps partway through. The connection to the model is dropped by the sleep, and Sortio now notices instead of waiting on it forever. Restarting the same sort picks up from where it stopped.',
      'Automations assigned to a Space run as one pipeline, so a rule that renames or moves a file hands the new location to the next rule rather than the old one.',
    ],
  },
  {
    version: 'v8.48.0',
    date: '2026-08-19',
    headline: 'The Windows installer is now code signed',
    items: [
      'Downloading Sortio on Windows no longer trips the SmartScreen "unrecognized app" warning, and Defender no longer quarantines the installer. It is signed through Azure Artifact Signing and published under Marcus Graves.',
      'If you kept a copy Windows had flagged, delete it and download fresh.',
    ],
  },
  {
    version: 'v8.47.0',
    date: '2026-08-18',
    headline: 'Sorts no longer nest under a copy of the folder',
    items: [
      'Sorting a folder named Probate could file things into Probate/Probate/... instead of Probate/... . Sortio now recognizes the repeated folder name and removes it, and the preview shows the destination that will actually be used. Nothing was ever lost to this, and affected sorts can be reversed from History.',
      'A session revoked on the server now prompts you to sign back in, rather than retrying quietly in the background while cloud features went missing.',
    ],
  },
  {
    version: 'v8.46.2',
    date: '2026-08-18',
    headline: 'A bad PDF can no longer take the app down',
    items: [
      'PDF page rendering for OCR runs in an isolated helper process, so a malformed file is skipped and logged instead of crashing Sortio during indexing.',
      'Indexing now records each file as it is processed, so any future crash can be traced to the file that caused it.',
    ],
  },
  {
    version: 'v8.46.0',
    date: '2026-08-17',
    headline: 'Safer sorting and better recovery',
    items: [
      'Every file move claims its destination atomically, closing a rare race where a file written by another program at the same instant could be overwritten.',
      'Sorts are journaled as they run, so an interrupted sort (crash, force quit, power loss) is still fully undoable from History at the next launch.',
      'The AI planner rejects folder structures too coarse for the folder being sorted, for example 3 folders for 800 files, and falls back to the standard engine.',
      'Automatic pre-sort backups now cover sorts up to 1 GB by default, up from 500 MB. Set the limit to 0 in Settings for unlimited.',
    ],
  },
  {
    version: 'v8.45.0',
    date: '2026-08-16',
    headline: 'Switch AI modes from the title bar',
    items: [
      'The title bar now shows which AI is actually running (Sortio Cloud, Offline Mode, or your own key) and is a one-click switcher: pick a mode and Sortio takes you to the right settings with your choice applied.',
      'Local Model is now called Offline Mode across the app, matching this site and the docs.',
      'The account button in the title bar shows your own account and opens Account settings.',
      'Account deletion now uses a two-step email-confirmed flow, so a stray click can never erase your data.',
      'Security hardening: sign-in callbacks are verified against a one-time code, and backup restore rejects files that try to escape the restore folder.',
    ],
  },
  {
    version: 'v8.44.1',
    date: '2026-08-16',
    headline: 'Content analysis is now opt-in',
    items: [
      'Sortio no longer reads file contents unless you turn content analysis on (Settings, General, or the Analyze button on the Sort page). It ships OFF for everyone.',
      'Sorting by name, folder, and metadata works exactly as before with the setting off.',
      'In-app descriptions of what leaves your device were rewritten for accuracy in all 7 languages.',
    ],
  },
  {
    version: 'v8.43.0',
    date: '2026-08-15',
    headline: 'Smarter document understanding',
    items: [
      'The indexing pipeline extracts entities from documents more accurately.',
      'After a sort finishes, a digest summarizes what moved where.',
    ],
  },
  {
    version: 'v8.42.1',
    date: '2026-08-14',
    headline: 'Automations now cover subfolders',
    items: [
      'Automations scan subfolders of a watched Space instead of only the top level.',
      'Fixed: extension rules written without a dot ("pdf" instead of ".pdf") never matched. Both forms now work.',
      'The path-safety guard now checks the destination of every file in a run, not just the first.',
    ],
  },
  {
    version: 'v8.41.2',
    date: '2026-08-12',
    headline: 'OCR on Windows fixed',
    items: [
      'Fixed a Windows path-handling bug that made OCR fail on most systems. Scanned PDFs and images are now read correctly on Windows.',
      'Files indexed before this fix need one re-index to pick up their text.',
    ],
  },
  {
    version: 'v8.41.0',
    date: '2026-08-09',
    headline: 'OCR language detection',
    items: [
      'OCR now detects each document’s language instead of assuming one, improving text extraction for scans in any of the languages Sortio supports.',
      'Readability reporting is more honest about which files could not be read and why.',
    ],
  },
  {
    version: 'v8.40.0',
    date: '2026-08-07',
    headline: 'Sorting engine v2',
    items: [
      'Sorts now plan the folder structure first, then assign files to it, producing more consistent results on large folders.',
      'Large sorts run up to 7x faster.',
    ],
  },
  {
    version: 'v8.39.0',
    date: '2026-07-31',
    headline: 'Search repairs itself',
    items: [
      'Fixed: the search index could become corrupted without any error, silently dropping files from results. Sortio now detects a damaged index and rebuilds it automatically.',
    ],
  },
  {
    version: 'v8.37.0',
    date: '2026-07-26',
    headline: 'Sorting streams results as they arrive',
    items: [
      'Fixed a connection issue that made desktop sorts fall back to a slower, all-at-once path. Plans now stream in as they are generated.',
    ],
  },
  {
    version: 'v8.34.0',
    date: '2026-07-25',
    headline: 'Undo reports the truth',
    items: [
      'Fixed: Undo could report success while restoring nothing if its backup data was missing. Undo now verifies each restored file and reports the real count.',
    ],
  },
  {
    version: 'v8.25.0',
    date: '2026-06-19',
    headline: 'French support and more reliable indexing',
    items: [
      'New: French (Français) interface language. Sortio is now available in 7 languages. Choose yours in Settings.',
      'Fixed: a file could occasionally get stuck while indexing (most often with accented or non-Latin content). Indexing now skips a problem file instead of stalling the rest.',
      'Indexing is more resilient: per-file timeouts, bounded large files, and progress that recovers on its own if a run stalls.',
    ],
  },
  {
    version: 'v8.21.0',
    date: '2026-06-09',
    headline: 'Get Sortio covered by your company',
    items: [
      'Using Sortio for work? A new prompt drafts a ready-to-send request to your manager for a Sortio Team license, or you can set up a team yourself.',
      'Stability and polish improvements.',
    ],
  },
  {
    version: 'v8.20.0',
    date: '2026-06-08',
    headline: 'LM Studio support for local AI',
    items: [
      'Local Model now lets you choose an engine: Ollama or LM Studio. LM Studio runs open-weight models on your machine through an OpenAI-compatible local server.',
      'Sortio auto-detects a running LM Studio server (default localhost:1234) and lists your loaded models; a Server URL field covers non-default ports.',
      'Same privacy and cost story as Ollama: everything runs on your machine, nothing is sent to Sortio or any cloud, zero credits consumed, works fully offline.',
      'Sorting, Spaces, and Automations all work with an LM Studio model. Setup guide at getsortio.com/docs/local-models.',
    ],
  },
  {
    version: 'v8.19.0',
    date: '2026-06-08',
    headline: 'Configurable backup size limit and backup fixes',
    items: [
      'The pre-sort safety backup cap (previously fixed at 500 MB) is now adjustable in Settings, with 0 for no limit. Undo works regardless of this setting.',
      'Fixed: pre-sort backups now correctly include files in subfolders. Previously, sorting with "Include Subfolders" on could silently skip the backup.',
    ],
  },
  {
    version: 'v8.18.0',
    date: '2026-06-06',
    headline: 'Automations that run themselves, plus a redesigned Spaces page',
    items: [
      'A Space can now auto-run its automations whenever files are added to its folder, watched in the background, with one-click Undo on every run.',
      'Three clear triggers: run automations manually, when you open a Space, or whenever files are added.',
      'Spaces page redesigned with a focused command bar and organized tabs (Automations, Contents, Auto-tag).',
      'Safety guard: Sortio now refuses to organize, watch, or move files out of system or root folders (like C:\\Windows\\System32, /System, or your home folder root).',
    ],
  },
  {
    version: 'v8.17.1',
    date: '2026-06-05',
    headline: 'Data-safety fixes for sorts and automations',
    items: [
      'Re-running a sort or automation no longer appends " (1)" to files that are already in the right place.',
      'File moves and renames can never overwrite a different file that shares a name. Sortio keeps both files instead of replacing one.',
      'Automation Rename actions now run reliably.',
      'Removed the non-working Copy and Delete actions from the automation builder.',
    ],
  },
  {
    version: 'v8.17.0',
    date: '2026-06-04',
    headline: 'Sortio now speaks 6 languages',
    items: [
      'Choose your interface language in Settings: English, Chinese (Simplified and Traditional), Korean, German, or Spanish.',
      'A separate content-language setting controls the language the AI uses for new folder names and sort feedback.',
      'Rule pack import works end to end again, and imported rules show up in Automations.',
      'Automation templates support fallback defaults like {file.exif.datetime|Other}, so files missing a field no longer stop the whole run.',
    ],
  },
  {
    version: 'v8.16.1',
    date: '2026-06-02',
    headline: 'Subscription display fix',
    items: [
      'Fixed a bug where your plan could show as "Free" after reopening the app following some time away, even with an active subscription. Sortio now refreshes your session automatically.',
      'Added a quick one-tap "How did you find Sortio?" prompt after onboarding.',
    ],
  },
  {
    version: 'v8.16.0',
    date: '2026-05-31',
    headline: 'Content-aware sorting and a clearer preview',
    items: [
      'When your prompt needs what is inside your files, Sortio offers to analyze contents and sorts automatically once it is ready, with a clear "reading your files" indicator.',
      'Redesigned sort preview shows files in a nested folder tree, so you can see the structure at a glance and skip individual files or whole folders.',
      'If your own AI (BYOK or Ollama) errors, you get a guided recovery panel instead of a raw error.',
      'Fixed a freeze when selecting certain folders, plus clearer plan-accurate cost display and post-sort suggestions to turn a sort into a reusable automation.',
    ],
  },
  {
    version: 'v8.15.2',
    date: '2026-05-28',
    headline: 'Consistent folders across large multi-batch sorts',
    items: [
      'Large sorts that fan out across multiple AI calls now reuse the folder choices made by earlier batches instead of inventing near-identical parallel folders.',
      'Sorts of larger folders (around 500+ files) end up with fewer, more meaningful folders and flatter, more stable hierarchies.',
    ],
  },
  {
    version: 'v8.15.1',
    date: '2026-05-28',
    headline: 'Zotero integration',
    items: [
      'Sort PDFs into Zotero collections: Sortio extracts citation metadata (title, authors, date, DOI, journal), classifies each PDF into the right collection, and creates Zotero items with linked-file attachments.',
      'Authenticate with a paste-the-key flow using a key from zotero.org/settings/keys; slash-style targets create properly nested subcollections.',
      'Fixed per-file dates being dropped from sort prompts, which made date-organized sorts collapse into "Unknown_Date" buckets.',
    ],
  },
  {
    version: 'v8.15.0',
    date: '2026-05-27',
    headline: 'On-change automations',
    items: [
      'Rules assigned to a Space with the "On file change" trigger now run automatically when a file is added or modified in the watched folder.',
      'Fixed the onboarding screen reappearing every time you opened the app.',
    ],
  },
  {
    version: 'v8.14.0',
    date: '2026-05-26',
    headline: 'OpenRouter support',
    items: [
      'Bring your own OpenRouter key to sort with Claude, GPT, Gemini, Llama, and more through a single account.',
      'Local Model and Bring-Your-Own-Key sorts now run entirely on your chosen engine, so your files never pass through Sortio Cloud.',
      'Fixed an upgrade prompt that could appear when a personal key hit its own provider\'s limit.',
    ],
  },
  {
    version: 'v8.13.2',
    date: '2026-05-18',
    headline: 'Billing and settings fixes',
    items: [
      'Manage Subscription now navigates correctly without reloading the entire app.',
      'Fixed a server error when opening Manage Billing (Stripe customer portal).',
      'Account is now the first tab when opening Settings.',
    ],
  },
  {
    version: 'v8.13.1',
    date: '2026-05-17',
    headline: 'Privacy controls in Settings',
    items: [
      'New Privacy panel in Settings: telemetry, analytics, and marketing email opt-in toggles, plus a data-export request flow.',
      'Edit your profile name in Account Settings.',
      'Subscription emails: an auto-renew acknowledgement on signup and a pre-renewal reminder for annual plans.',
    ],
  },
  {
    version: 'v8.13.0',
    date: '2026-05-16',
    headline: 'Delete account from Settings',
    items: [
      'Settings now has a Delete Account option. Typing your email confirms; deletion removes your account and associated data. Files on your computer are not affected.',
      'Community automations on the Discover page are now a Pro feature; Pro users keep one-click import.',
    ],
  },
  {
    version: 'v8.12.1',
    date: '2026-05-12',
    headline: 'Hero copy refresh and watch-folder reliability fixes',
    items: [
      'Updated download links across the site to v8.12.1.',
      'Watch folder reliability improvements on macOS Sonoma and Sequoia.',
      'Hero subhead now reads "each file" instead of "each PDF" because Sortio handles any file type.',
    ],
  },
  {
    version: 'v8.12.0',
    date: '2026-05-01',
    headline: 'Vertical landing pages and FAQ rich snippets',
    items: [
      'Shipped five new vertical landing pages: law firms, real estate, small business back office, consultants, engineering, and personal archivists.',
      'Baked vertical FAQPage JSON-LD into the static build so rich snippets appear for vertical queries.',
      'Sitemap regenerated to include the new landing pages.',
    ],
  },
  {
    version: 'v8.11.0',
    date: '2026-04-15',
    headline: 'AI Rule Builder',
    items: [
      'New AI Rule Builder generates reusable sorting rules from a natural-language description.',
      'Run a rule once and reuse it forever, without consuming AI credits per file.',
      'Recommended for archives over 5,000 files. The rule runs locally and scales without AI calls.',
    ],
  },
  {
    version: 'v8.10.0',
    date: '2026-03-20',
    headline: 'Knowledge Graph indexing for Spaces',
    items: [
      'Sortio now extracts entities (people, organizations, dates, dollar amounts) from your files and indexes them per Space.',
      'Use {entity.organization}, {entity.date}, and other tokens in automation rules.',
      'Knowledge Graph indexing runs on your device.',
    ],
  },
];

const LATEST = releases[0];

const ChangelogPage = () => {
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
        { '@type': 'ListItem', position: 2, name: 'Changelog', item: 'https://www.getsortio.com/changelog' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Sortio Changelog',
      url: 'https://www.getsortio.com/changelog',
      dateModified: LATEST.date,
      about: {
        '@type': 'SoftwareApplication',
        name: 'Sortio',
        operatingSystem: 'macOS, Windows, Linux',
        softwareVersion: LATEST.version.replace('v', ''),
        url: 'https://www.getsortio.com',
      },
    },
  ];

  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <SEOHead
        title="Sortio Changelog: Release Notes and Product Updates"
        description="Release notes and product updates for Sortio, the AI file sorter for Mac, Windows, and Linux. New features, fixes, and rollouts by version."
        canonical="https://www.getsortio.com/changelog"
        schema={schema}
        lastModified={LATEST.date}
      />

      <div className="container mx-auto px-6 max-w-3xl relative z-10">
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-12 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <Tag className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            Sortio <span className="apple-gradient-text">Changelog</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-xl">
            What is new in Sortio. Release notes for the desktop app and the marketing site.
          </p>
          <p className="text-apple-400 mt-3 text-center text-sm">
            Latest version: <span className="text-apple-100 font-medium">{LATEST.version}</span>, released {LATEST.date}.{' '}
            <Link to="/download" className="text-sortio-primary hover:text-sortio-primary/80 transition-colors">Download it here</Link>
            {' '}or see{' '}
            <a
              href="https://github.com/canonicalmg/Sortio-Releases/releases"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sortio-primary hover:text-sortio-primary/80 transition-colors"
            >
              full release notes on GitHub
            </a>.
          </p>
        </div>

        <div className="space-y-8">
          {releases.map((r) => (
            <article key={r.version} className="apple-panel p-6 rounded-apple">
              <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                <h2 className="text-xl font-medium text-apple-50">{r.version}</h2>
                <span className="text-xs text-apple-400">{r.date}</span>
              </div>
              <h3 className="text-base text-sortio-primary mb-3">{r.headline}</h3>
              <ul className="space-y-2 list-disc list-inside marker:text-apple-400">
                {r.items.map((item, idx) => (
                  <li key={idx} className="text-apple-300 text-sm leading-relaxed">{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChangelogPage;
